import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MessageCircle, MapPin, CalendarDays, Hotel, Sparkles } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { PackageCard } from "@/components/PackageCard";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { api } from "@/lib/apiClient";
import { waLink } from "@/data/site";
import Seo, { orgJsonLd } from "@/components/Seo";

export default function DestinationDetail() {
  const { slug } = useParams();
  const [d, setD] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    setD(null); setNotFound(false);
    api.get(`/destinations/${slug}`).then((r) => {
      setD(r.data);
      api.get("/itineraries", { params: { published: true, region: r.data.region } }).then((res) => setRelated(res.data.slice(0, 3)));
    }).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="pt-40 pb-32 text-center">
      <p className="font-display text-4xl text-[#2A1808]">Destination not found</p>
      <Link to="/destinations" className="inline-block mt-6 text-[#F49E1A] font-semibold">← All destinations</Link>
    </div>
  );
  if (!d) return <div className="pt-40 pb-32 text-center text-[#8A6B47]">Loading…</div>;

  const jsonLd = {
    "@context": "https://schema.org", "@type": "TouristDestination",
    name: d.name, description: d.intro, image: d.hero_image,
    ...(d.faqs?.length ? {} : {}),
  };
  const Section = ({ icon: Icon, title, children }) => (
    <Reveal>
      <div className="border-t border-[#553204]/10 py-8">
        <h2 className="font-display text-2xl sm:text-3xl text-[#2A1808] flex items-center gap-3"><Icon size={22} className="text-[#F49E1A]" /> {title}</h2>
        <div className="mt-4 text-[#553204] leading-relaxed">{children}</div>
      </div>
    </Reveal>
  );

  return (
    <div className="bg-[#FFF7EC]">
      <Seo title={`${d.name} Tours & Travel Guide`} description={d.intro?.slice(0, 155)} image={d.hero_image} type="article" jsonLd={jsonLd} />
      <div className="relative h-[72vh] min-h-[460px]">
        <img src={d.hero_image} alt={d.name} className="h-full w-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(42,24,8,0.5) 0%, rgba(42,24,8,0.15) 40%, rgba(42,24,8,0.8) 100%)" }} />
        <div className="absolute inset-0 flex flex-col justify-end">
          <div className="max-w-7xl mx-auto w-full px-4 sm:px-8 lg:px-12 pb-12">
            <Link to="/destinations" className="inline-flex items-center gap-2 text-[#F9EFE1]/80 text-sm mb-5 hover:text-white"><ArrowLeft size={16} /> Destinations</Link>
            <p className="text-[#F49E1A] uppercase tracking-widest text-xs">{d.region}</p>
            <h1 className="font-display text-5xl sm:text-7xl font-medium text-white mt-2 leading-[1.02]">{d.name}</h1>
            <p className="mt-4 font-display italic text-2xl text-[#F9EFE1]/90">{d.tagline}</p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-8 lg:px-12 py-14">
        <Reveal><p className="text-xl leading-relaxed text-[#553204] font-display">{d.intro}</p></Reveal>

        {d.highlights?.length > 0 && (
          <Section icon={Sparkles} title="What to see">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
              {d.highlights.map((h, i) => <li key={`${h}-${i}`} className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[#F49E1A]" /> {h}</li>)}
            </ul>
          </Section>
        )}
        {d.experiences?.length > 0 && (
          <Section icon={MapPin} title="The experience">
            <div className="flex flex-wrap gap-2">
              {d.experiences.map((e, i) => <span key={`${e}-${i}`} className="rounded-full bg-[#F9EFE1] border border-[#553204]/10 px-4 py-2 text-sm">{e}</span>)}
            </div>
          </Section>
        )}
        {d.best_time && <Section icon={CalendarDays} title="When to go"><p>{d.best_time}</p>{d.how_to_reach && <p className="mt-2">{d.how_to_reach}</p>}</Section>}
        {d.where_to_stay && <Section icon={Hotel} title="Where to stay"><p>{d.where_to_stay}</p></Section>}

        {d.faqs?.length > 0 && (
          <Reveal>
            <div className="border-t border-[#553204]/10 py-8">
              <h2 className="font-display text-2xl sm:text-3xl text-[#2A1808] mb-4">Good to know</h2>
              <Accordion type="single" collapsible className="space-y-2">
                {d.faqs.map((f, i) => (
                  <AccordionItem key={`${f.q}-${i}`} value={`f-${i}`} className="rounded-md border border-[#553204]/10 bg-[#FFFBF5] px-5">
                    <AccordionTrigger className="text-left font-display text-lg text-[#2A1808] hover:no-underline">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-[#553204] text-sm pb-4">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </Reveal>
        )}
      </div>

      {related.length > 0 && (
        <section className="bg-[#F9EFE1] py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
            <h2 className="font-display text-3xl sm:text-4xl text-[#2A1808] mb-8">Curated journeys in {d.name}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((it) => <PackageCard key={it.slug} item={it} />)}
            </div>
          </div>
        </section>
      )}

      <section className="bg-[#FFF7EC] py-16 text-center">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="font-display text-3xl text-[#2A1808]">Plan your {d.name} journey</h2>
          <p className="mt-3 text-[#553204]">Tell us your dates and group — we'll tailor an itemised plan within a day.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <a href={waLink(`Hi Vacanza Go, I'd like to plan a trip to ${d.name}.`)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full btn-orange px-7 py-3 text-sm"><MessageCircle size={16} /> Plan on WhatsApp</a>
            <Link to="/packages" className="rounded-full border border-[#553204]/20 px-7 py-3 text-sm text-[#2A1808] hover:bg-[#553204]/5">Browse Journeys</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
